// TuroRequirementsPage.jsx
import React, { useEffect } from "react";

/**
 * Minimal Helmet-like helpers without extra deps.
 * If you already use react-helmet, replace <SeoHead/> with <Helmet>...</Helmet>.
 */
function SeoHead() {
  useEffect(() => {
    const title =
      "Turo Requirements to Rent Your Car (2026) – Turo Car Rental Requirements & Is Turo a Good Way to Rent a Car?";
    const desc =
      "Complete 2026 guide to Turo requirements to rent your car. Learn Turo car rental requirements, host criteria, and whether Turo is a good way to rent a car—plus a free Turo calculator.";
    const url = "https://www.diviseed.com/turo-requirements-to-rent-your-car";
    const image = "https://www.diviseed.com/og/turo-requirements-2025.png"; // optional

    document.title = title;

    const setMeta = (attr, key, value) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    setMeta("name", "description", desc);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:type", "article");
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", image);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", image);

    // Canonical link
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", url);

    // JSON-LD FAQ Schema (include the target phrases)
    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.id = "faq-jsonld";
    faqScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Is Turo a good way to rent a car?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Turo can be a good way to rent a car if you prioritize unique vehicles, flexible pickup options, and competitive pricing. Costs and availability vary by city and season, so comparing options and reviewing host ratings helps ensure a good experience."
          }
        },
        {
          "@type": "Question",
          "name": "What are the Turo requirements to rent your car?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Generally, your vehicle should have a clean title, be well-maintained, meet age and mileage guidelines, be properly registered, and have no open safety recalls. Hosts must meet age, license, and verification requirements; insurance via auto insurance providers, tint.ai, or a compliant commercial policy."
          }
        },
        {
          "@type": "Question",
          "name": "What are the Turo car rental requirements for hosts?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Hosts typically must be 21+ with a valid driver’s license and pass Turo’s screening. Vehicles should meet model year and mileage thresholds, be safe and roadworthy, have current registration, and be free of salvage titles and open recalls."
          }
        },
        {
          "@type": "Question",
          "name": "Do I need commercial rental insurance to host on Turo?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Not necessarily—Turo offers several host protection plans that include liability coverage. You can also use your own compliant commercial policy if preferred."
          }
        }
      ]
    });
    document.head.appendChild(faqScript);

    // Cleanup on unmount
    return () => {
      const s = document.getElementById("faq-jsonld");
      if (s) s.remove();
    };
  }, []);

  return null;
}

function Section({ title, children, id }) {
  return (
    <section id={id} className="max-w-4xl mx-auto px-4 py-6">
      <h2 className="text-xl sm:text-2xl font-semibold mb-3">{title}</h2>
      <div className="prose prose-sm sm:prose lg:prose-lg max-w-none prose-headings:scroll-mt-20">
        {children}
      </div>
    </section>
  );
}

export default function TuroRequirementsPage() {
  return (
    <div className="w-full">
      <SeoHead />

      {/* Hero */}
      <header className="bg-gradient-to-b from-white to-gray-50 border-b">
        <div className="max-w-5xl mx-auto px-4 py-10">
          <p className="text-xs uppercase tracking-wide text-blue-600 font-semibold">
            2026 Host & Renter Guide
          </p>
          <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight mt-2">
            Turo Requirements to Rent Your Car (2026 Guide)
          </h1>
          <p className="text-gray-600 mt-3 max-w-3xl">
            Learn the <strong>turo requirements to rent your car</strong>, the most important{" "}
            <strong>turo car rental requirements</strong> for hosts, and whether{" "}
            <strong>is turo a good way to rent a car</strong> for your situation.
            We’ll also show you how to estimate profits in your market with Diviseed’s
            data-backed tools.
          </p>

          {/* CTA Row */}
          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <a
              href="https://www.diviseed.com"
              className="inline-flex items-center justify-center rounded-lg px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 shadow-sm"
            >
              Get Free Turo Calculator
            </a>
            <a
              href="#requirements"
              className="inline-flex items-center justify-center rounded-lg px-4 py-2 border border-gray-300 hover:bg-gray-50"
            >
              See the Requirements
            </a>
          </div>

          {/* Quick Nav */}
          <nav className="mt-6 text-sm text-gray-600 flex flex-wrap gap-x-4 gap-y-1">
            <a href="#is-turo-good" className="hover:text-black">
              Is Turo a good way to rent a car?
            </a>
            <span>·</span>
            <a href="#host-req" className="hover:text-black">Host requirements</a>
            <span>·</span>
            <a href="#vehicle-req" className="hover:text-black">Vehicle requirements</a>
            <span>·</span>
            <a href="#rental-req" className="hover:text-black">Turo car rental requirements</a>
            <span>·</span>
            <a href="#prohibited" className="hover:text-black">Prohibited vehicles</a>
            <span>·</span>
            <a href="#listing" className="hover:text-black">Listing checklist</a>
            <span>·</span>
            <a href="#faq" className="hover:text-black">FAQ</a>
          </nav>
        </div>
      </header>

      {/* Content */}
      <div id="requirements" />

      {/* NEW: Directly addresses the exact query */}
      <Section
        id="is-turo-good"
        title="Is Turo a Good Way to Rent a Car?"
      >
        <p>
          For many travelers, <strong>is Turo a good way to rent a car</strong> depends on
          price, convenience, and the type of vehicle you want. Turo often offers
          more unique options than traditional agencies, flexible pickup,
          and competitive pricing—especially outside airport desks. To decide,
          compare total cost (daily rate, trip fees, extras) and check host reviews.
        </p>
        <p className="mt-2">
          If you’re a host, the better question is profitability. Diviseed’s free
          calculator estimates demand, average daily rate (ADR), and seasonality
          for your city—so you can see if hosting is worth it.
        </p>
        <a
          href="https://www.diviseed.com"
          className="mt-3 inline-flex items-center rounded-md bg-green-600 text-white px-3 py-2 hover:bg-green-700"
        >
          Try the Free Turo Calculator →
        </a>
      </Section>

      <Section id="host-req" title="1) Host Requirements">
        <ul className="list-disc pl-5 space-y-2">
          <li><strong>Age:</strong> 21+ in the U.S. (local exceptions may apply).</li>
          <li><strong>Driver’s license:</strong> Valid U.S. or international license.</li>
          <li><strong>Background check:</strong> You must pass Turo’s screening.</li>
          <li>
            <strong>Insurance:</strong> Choose a Turo host protection plan or bring a
            compliant commercial policy.
          </li>
        </ul>
      </Section>

      <Section id="vehicle-req" title="2) Vehicle Requirements">
        <ul className="list-disc pl-5 space-y-2">
          <li><strong>Model year:</strong> Typically within the last ~12 years (classics may be exempt).</li>
          <li><strong>Mileage:</strong> Generally under ~130,000 miles at listing time.</li>
          <li><strong>Condition:</strong> Clean title, no salvage; safe, well-maintained, no open safety recalls.</li>
          <li><strong>Registration:</strong> Registered in your name or you have legal right to rent it.</li>
          <li><strong>Safety:</strong> Seatbelts, lights, brakes, tires, and all safety equipment functioning.</li>
        </ul>
        <p className="text-sm text-gray-500 mt-3">
          Note: Requirements can vary by market and change over time. Always verify on Turo before listing.
        </p>
      </Section>

      {/* NEW: Exact-phrase section for “turo car rental requirements” */}
      <Section id="rental-req" title="3) Turo Car Rental Requirements (Quick Checklist)">
        <div className="rounded-lg border bg-white p-4">
          <ul className="list-disc pl-5 space-y-2">
            <li>Clean, non-salvage title; no open safety recalls.</li>
            <li>Meets model year and mileage guidelines at the time of listing.</li>
            <li>Proper registration and ability to legally share the vehicle.</li>
            <li>Roadworthy condition with working safety equipment.</li>
            <li>Host meets age, license, and verification requirements.</li>
            <li>insurance via auto insurance providers, tint.ai, or a compliant commercial policy.</li>
          </ul>
        </div>
      </Section>

      <Section id="prohibited" title="4) Prohibited or Limited Vehicles">
        <ul className="list-disc pl-5 space-y-2">
          <li>Motorcycles, RVs, and certain exotics (varies by region/policy).</li>
          <li>Vehicles with branded/salvage titles.</li>
          <li>Vehicles with unresolved safety recalls.</li>
        </ul>
      </Section>

      <Section id="listing" title="5) Listing Checklist (Quick Wins)">
        <ul className="list-disc pl-5 space-y-2">
          <li><strong>Photos:</strong> Clear, daylight shots from multiple angles; clean interior.</li>
          <li><strong>Description:</strong> Honest details and any quirks/limitations.</li>
          <li><strong>Pricing:</strong> Start market-competitive and adjust with data.</li>
        </ul>
        <div className="mt-4 rounded-lg border bg-white p-4">
          <h3 className="font-semibold mb-2">
            Pro Tip: Validate profit before you list
          </h3>
          <p className="text-gray-700">
            Meeting the <strong>turo requirements to rent your car</strong> is step one. The real key is{" "}
            <strong>profitability</strong>. Diviseed analyzes demand, ADR, and seasonality so you can list
            with confidence.
          </p>
          <a
            href="https://www.diviseed.com"
            className="mt-3 inline-flex items-center rounded-md bg-green-600 text-white px-3 py-2 hover:bg-green-700"
          >
            Run a Free Market Check →
          </a>
        </div>
      </Section>

      <Section id="compare" title="6) Turo vs. Getaround vs. HyreCar (At a Glance)">
        <div className="overflow-x-auto">
          <table className="min-w-full border text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-2 text-left">Platform</th>
                <th className="p-2 text-left">Typical Vehicle Age</th>
                <th className="p-2 text-left">Mileage Limit</th>
                <th className="p-2 text-left">Insurance Option</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t">
                <td className="p-2">Turo</td>
                <td className="p-2">~12 years (classic exceptions)</td>
                <td className="p-2">~130k</td>
                <td className="p-2">Host plans or commercial</td>
              </tr>
              <tr className="border-t">
                <td className="p-2">Getaround</td>
                <td className="p-2">Often newer vehicles preferred</td>
                <td className="p-2">Varies</td>
                <td className="p-2">Platform coverage</td>
              </tr>
              <tr className="border-t">
                <td className="p-2">HyreCar</td>
                <td className="p-2">Used vehicles allowed</td>
                <td className="p-2">Varies</td>
                <td className="p-2">Platform coverage</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      <Section id="faq" title="7) FAQ – Turo Requirements">
        <details className="border rounded-md p-3 mb-2">
          <summary className="font-medium cursor-pointer">
            Is Turo a good way to rent a car?
          </summary>
          <p className="mt-2">
            It can be—especially if you want specific models, flexible pickup, or competitive pricing.
            Compare total cost, check reviews, and book from highly rated hosts for the best experience.
          </p>
        </details>
        <details className="border rounded-md p-3 mb-2">
          <summary className="font-medium cursor-pointer">
            What are the Turo requirements to rent your car?
          </summary>
          <p className="mt-2">
            Clean title, no open recalls, within age/mileage guidelines, roadworthy and registered.
            Hosts must meet age, license, and verification requirements. Insurance via Turo plans or
            a compliant commercial policy.
          </p>
        </details>
        <details className="border rounded-md p-3">
          <summary className="font-medium cursor-pointer">
            What are the Turo car rental requirements for hosts?
          </summary>
          <p className="mt-2">
            Typically 21+, valid license, pass screening; vehicles must be safe, registered, and meet
            model year/mileage thresholds (with classic exceptions).
          </p>
        </details>
      </Section>

      {/* Bottom CTA */}
      <section className="max-w-4xl mx-auto px-4 pb-12">
        <div className="rounded-xl border bg-blue-50 p-6 text-center">
          <h3 className="text-lg font-semibold">Ready to see if it’ll be profitable?</h3>
          <p className="text-gray-700 mt-2">
            Use Diviseed to forecast earnings, demand, and ROI for your car and city.
          </p>
          <a
            href="https://www.diviseed.com"
            className="mt-4 inline-flex items-center justify-center rounded-lg px-4 py-2 bg-blue-600 text-white hover:bg-blue-700"
          >
            Analyze My Car →
          </a>
        </div>
      </section>
    </div>
  );
}
